import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { formatDateTimeRange } from '@modules/common/utils';
import { useCallback } from 'react';
import { QuantifyQueryEventProps } from 'v2/analytics/events';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';
import { useQuantifyStore } from '../store/quantifyStore';
import { trackQuantifyQueryEvents } from '../utils';

export const useTriggerQuantifyQueryAnalyticsEvent = () => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { name, href, scope } = useAnalysisStore(
    (analysis) => ({
      name: analysis.title,
      href: analysis.href,
      scope: analysis.scope,
    }),
    shallow,
  );

  const {
    groupByField,
    duration,
    measuredAsCriteria,
    measuredAsFormula,
    whereClause,
    denominatorWhereClause,
  } = useQuantifyStore(
    ({ query }) => ({
      groupByField: query.groupByField?.displayName,
      duration: formatDateTimeRange(query.duration),
      measuredAsCriteria: query.formulaQuery?.criteria?.displayName,
      measuredAsFormula: query.formulaQuery?.eqlFunction,
      whereClause: query?.whereClause,
      denominatorWhereClause: query.formulaQuery?.denominatorWhereClause,
    }),
    shallow,
  );

  const { eql: baseWhereClauseEQL } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause: whereClause ?? EmptyWhereClause,
      expressionType: ExpressionType.Records,
    },
  });

  const { eql: denominatorWhereClauseEQL } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause: denominatorWhereClause ?? EmptyWhereClause,
      expressionType: ExpressionType.Records,
    },
  });

  return useCallback(
    (
      eventName: QuantifyQueryEventProps['eventName'],
      query?: Partial<QuantifyQueryEventProps['query']>,
    ) => {
      trackQuantifyQueryEvents({
        eventName: eventName,
        query: {
          showMeLabel: query?.showMeLabel ?? groupByField,
          queryString: query?.queryString ?? baseWhereClauseEQL,
          duration: query?.duration ?? duration,
          measuredAsCriteria: query?.measuredAsCriteria ?? measuredAsCriteria,
          measuredAsFormula: query?.measuredAsFormula ?? measuredAsFormula,
          denominatorWhereClauseEQL:
            query?.denominatorWhereClauseEQL ?? denominatorWhereClauseEQL,
        },
        name,
        link: href,
        scope: mapFromEdgeScope(scope),
      });
    },
    [
      href,
      name,
      scope,
      baseWhereClauseEQL,
      groupByField,
      duration,
      measuredAsCriteria,
      measuredAsFormula,
      denominatorWhereClauseEQL,
    ],
  );
};
